import { useCallback } from "react";
import { User, Session } from "@supabase/supabase-js";
import { userStore } from "../../stores/User";
import { AccessLevel } from "../../../models/enums/AccessLevel";
import { getPrefixedStorageName } from "../../../utils/storagePrefix";

const useAuthSession = () => {
    const { updateUser } = userStore((state) => state);

    const saveSession = useCallback((user: User, session: Session) => {
        const userKey = getPrefixedStorageName("user");
        const sessionKey = getPrefixedStorageName("session");
        
        updateUser({
            info: user,
            session: session,
            authenticated: true,
            role: user.user_metadata?.role || (user.user_metadata?.accountType === 'store_owner' ? AccessLevel.Admin : AccessLevel.Public)
        });

        localStorage.setItem(userKey, JSON.stringify(user));
        localStorage.setItem(sessionKey, JSON.stringify(session));
    }, [updateUser]);

    const loadSession = useCallback(() => {
        const userKey = getPrefixedStorageName("user");
        const sessionKey = getPrefixedStorageName("session");
        const user = localStorage.getItem(userKey);
        const session = localStorage.getItem(sessionKey);
        
        if (user && session) {
            return {
                user: JSON.parse(user) as User,
                session: JSON.parse(session) as Session,
            };
        }
        return null;
    }, []);

    const clearSession = useCallback(() => {
        const userKey = getPrefixedStorageName("user");
        const sessionKey = getPrefixedStorageName("session");
        localStorage.removeItem(userKey);
        localStorage.removeItem(sessionKey);

        updateUser({
            info: new Object() as User,
            session: new Object() as Session,
            authenticated: false,
            role: AccessLevel.Public
        });
    }, [updateUser]);

    const checkSession = useCallback(() => {
        const session = loadSession();
        if (session && session.session && session.session.expires_at) {
            const currentTime = Math.floor(Date.now() / 1000);
            const timeUntilExpiry = session.session.expires_at - currentTime;
            // const minutesUntilExpiry = Math.floor(timeUntilExpiry / 60);

            if (session.session.expires_at > currentTime) {
                // console.log(`✅ [checkSession] Session valid, restoring user:`, {
                //     email: session.user.email,
                //     expiresIn: `${minutesUntilExpiry} minutes`
                // });
                updateUser({
                    info: session.user,
                    session: session.session,
                    authenticated: true,
                    role: session.user.user_metadata?.role || (session.user.user_metadata?.accountType === 'store_owner' ? AccessLevel.Admin : AccessLevel.Public)
                });
            } else {
                // Access token expired, but check if we have a refresh token
                // Don't immediately clear - let the API client handle refresh automatically
                
                // Only clear session if refresh token is also expired or missing
                if (!session.session.refresh_token) {
                    clearSession();
                    updateUser({
                        info: new Object() as User,
                        session: new Object() as Session,
                        authenticated: false,
                        role: AccessLevel.Public
                    });
                } else {
                    // Keep the session for automatic refresh
                    // The ApiClient will handle token refresh on next API call
                    updateUser({
                        info: session.user,
                        session: session.session,
                        authenticated: true, // Keep authenticated for auto-refresh
                        role: session.user.user_metadata?.role || (session.user.user_metadata?.accountType === 'store_owner' ? AccessLevel.Admin : AccessLevel.Public)
                    });
                }
            }
        } else {
            console.log(`❌ [checkSession] No valid session found in localStorage`);
            clearSession();
            updateUser({
                info: new Object() as User,
                session: new Object() as Session,
                authenticated: false,
                role: AccessLevel.Public
            });
        }
    }, [loadSession, clearSession, updateUser]);

    return {
        saveSession,
        clearSession,
        checkSession,
    };
};

export default useAuthSession;
